import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { theme } from "../../themes/theme";
import ProgressPieChart from "../../components/TableItems/ProgressPieChart";
import { useSelector } from "react-redux";
import { AppState } from "../../redux/store";
import { RuleUnitState } from "../../types/models/RuleUnitState";
import { useAlerts } from "../../hooks/useAlerts";
import { displayUserRole } from "../../utils/StringUtils";
import { RoleState } from "../../types/models/RoleState";
import MemberModal from "../your-teams/MemberModal.tsx";
import { MyTeamId } from "../your-teams";
import { TeamMemberProgress } from "../../types/models/TeamMember";
import { useTeams } from "../../hooks/useTeams";
import { TeamMembersState } from "../../types/models/TeamMembersState";

const UserTable: React.FC = () => {
  const { isLoading, refreshAlerts } = useAlerts(); // Call APIs to get alert rules
  const { filterAlertProgress } = useTeams();
  const currentTeam = {
    TeamId: MyTeamId,
    DisplayName: "",
    StartDate: "",
    EndDate: "",
    Members: [],
  };
  const teamState = useSelector<AppState, TeamMembersState>((state) => state.teamMembers);
  const ruleUnitState = useSelector<AppState, RuleUnitState>((state) => state.ruleUnit);
  const roleState = useSelector<AppState, RoleState>((state) => state.role);
  const alerts = ruleUnitState.baseAlertContentRevisionDetails
    ?.map((baseAlert) =>
      baseAlert.RuleInstances?.map((rule) => ({
        Metadata: rule.Metadata,
        RuleInstanceId: rule.RuleInstanceId,
      }))
    )
    .flat();

  const [openUserProgress, setOpenUserProgress] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<TeamMemberProgress | undefined>(
    teamState.userProgress
  );

  useEffect(() => {
    refreshAlerts();
    setSelectedUser(teamState.userProgress);
  }, []);

  const getPieChartForUserProgress = () => {
    const userProgress = teamState.myProgress;
    const total =
      userProgress?.alertCount && userProgress.acknowledgedCount
        ? userProgress.alertCount - userProgress?.acknowledgedCount
        : 0;
    return (
      <Box sx={{ position: "relative" }}>
        <ProgressPieChart
          total={userProgress?.alertCount ?? 0}
          highlighted={userProgress?.overdueCount ?? 0}
          completed={userProgress?.acknowledgedCount ?? 0}
          modal={false}
        />
        {total === 0 ? null : total > 0 && total < 10 ? (
          <span className="not-acknowledged-small-number">{total}</span>
        ) : (
          <span className="not-acknowledged-number">{total}</span>
        )}
      </Box>
    );
  };

  const SkeletonRow = () => {
    return (
      <TableRow>
        {Array.from({ length: 2 }, (_, i) => (
          <TableCell key={i} height={"52px"}>
            <Skeleton variant="text" height="1.5em" />
          </TableCell>
        ))}
        <TableCell key={"alert"} height={"52px"}>
          <Skeleton variant="circular" height="2.5em" width="2.5em" />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: theme.table.subHeaderBgColor }}>
            <TableRow>
              <TableCell>Role Bundles</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell align="center" width={"120px"}>
                Alerts
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                <SkeletonRow />
              </>
            ) : (
              <TableRow sx={{ cursor: "pointer" }} onClick={() => setOpenUserProgress(true)}>
                <TableCell>
                  {(teamState.myProgress?.roleBundles && teamState.myProgress.roleBundles.length > 0) ?
                    teamState.myProgress?.roleBundles?.map((bundle) => bundle.name)
                    .join(", ")
                    : (
                      <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>
                        No role bundles assigned
                      </Typography>)
                  }
                </TableCell>
                <TableCell>
                  {roleState.myRoles
                    ?.map((role) => displayUserRole(role.RoleId, roleState.knownRoles?.roles))
                    .join(", ")}
                </TableCell>
                <TableCell>{getPieChartForUserProgress()}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {teamState.userProgress && (
        <MemberModal
          alerts={alerts}
          currentTeam={currentTeam}
          onClose={() => setOpenUserProgress(false)}
          open={openUserProgress}
          selectedMember={selectedUser}
          setSelectedMember={setSelectedUser}
          user={teamState.userProgress}
          userProgress={filterAlertProgress(
            teamState.userProgress.progress ?? [],
            teamState.userProgress.roles ?? [],
            teamState.userProgress.locations ?? []
          )}
        />
      )}
    </>
  );
};

export default UserTable;
